import { useGetAuctionForms } from "apollo/hooks/queries";
import { composeProgress, isLoading } from "apollo/utils";
import {
  FILE_SCOPE,
  QUERY_PROGRESS_FAILED,
  QUERY_PROGRESS_NOT_STARTED,
  QUERY_PROGRESS_PENDING,
  QUERY_PROGRESS_SUCCEED,
} from "core/consts";
import { isFileV2 } from "core/model/files/utils";
import { getPatientName } from "core/model/patients";
import {
  Auction,
  AuctionForm,
  ClinicalConsultation,
  DocsFieldFeedback,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormStatusResponse,
  FileParams,
  FileState,
  FormMetadata,
  Patient,
  QueryProgress,
  ValueOf,
} from "core/types";
import {
  Button,
  Dropdown,
  Form,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  Spinner,
} from "ds/ui";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { useGetFilesFeatures } from "dsl/ecosystems/PatientFiles/useGetFilesFeatures";
import { getFileNameAndIsNewVersionAvailable } from "dsl/ecosystems/PatientFiles/utils";
import {
  IMPORT_AUCTION_ID,
  REIMPORT_AUCTION_ID,
} from "dsl/ecosystems/PatientForms/RehabForms/RehabFormWrapper";
import { RefreshCwIcon, SparklesIcon, XIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useWatch } from "react-hook-form";
import { useTranslations } from "translations";
import { useRecareDocsContext } from "../context";
import { DocsFeedbackPanel } from "./FeedbackPanel";
import { FormFooter } from "./Footer";
import { DocsFormFields } from "./FormFields";
import { HEADER_BANNERS } from "./HeaderBanners";
import { DocsModalSources } from "./Sources";
import { useDocsForm } from "./useDocsForm";
import {
  getManuallyEditedFieldKeys,
  getDocsFormIsFilled,
  isDischargeLetterFormName,
  REFRESH_MODE,
  RefreshMode,
} from "./utils";

export const DOCS_FORM_FOOTER_SCREENS = {
  DEFAULT: "default",
  DELETE_FORM_CONFIRMATION: "delete-form-confirmation",
  SAVE_FORM_CONFIRMATION: "save-form-confirmation",
  UPLOAD_PDF_CONFIRMATION: "upload-pdf-confirmation",
  WARN_OPEN_PDF: "warn-open-pdf",
  WARN_SEND_TO_HIS: "warn-send-to-his",
} as const;

export type DocsFormFooterScreen = ValueOf<typeof DOCS_FORM_FOOTER_SCREENS>;

export const useFetchFiles = ({
  auction,
  consultation,
  patient,
}: {
  auction: Auction | undefined;
  patient: Patient;
  consultation: ClinicalConsultation | undefined;
}) => {
  const [filesProgress, setFilesProgress] = useState<QueryProgress>(
    QUERY_PROGRESS_NOT_STARTED,
  );
  const [files, setFiles] = useState<{ name: string; file: FileState }[]>([]);
  const toast = useToast();
  const translations = useTranslations();

  const params: FileParams = auction
    ? `patient_id=${patient.id}&auction_id=${auction.id}`
    : consultation
    ? `patient_id=${patient.id}&clinical_consultation_id=${consultation.id}`
    : `patient_id=${patient.id}`;

  const { getFilesHISProgress, queryProgress, uploadedFilesV2 } =
    useGetFilesFeatures({
      action: undefined,
      externalId: patient.external_id,
      params,
      skipForms: true,
    });

  const filteredUploadedFilesV2 = useMemo(
    () =>
      uploadedFilesV2?.filter((file) =>
        isFileV2(file) ? file.file_scope !== FILE_SCOPE.ELIGIBILITY : true,
      ),
    [uploadedFilesV2],
  );

  useEffect(() => {
    (async () => {
      try {
        setFilesProgress(QUERY_PROGRESS_PENDING);
        const filesData = await Promise.all(
          filteredUploadedFilesV2?.map(async (file) => {
            const props = await getFileNameAndIsNewVersionAvailable({
              canAccessPatientData: true,
              file,
            });

            if (props.shouldBlur) {
              return null;
            }

            if (typeof props.value === "string" && file.updated_at) {
              return { file, name: props.value };
            }

            return null;
          }) ?? [],
        );
        if (filesData.truthy()) {
          setFiles(filesData.truthy());
        }
        setFilesProgress(QUERY_PROGRESS_SUCCEED);
      } catch (error) {
        toast({
          color: "danger",
          message: translations.general.error,
        });
        console.error("Error fetching files:", error);
        setFilesProgress(QUERY_PROGRESS_FAILED);
      }
    })();
  }, [filteredUploadedFilesV2]);

  return {
    files,
    filesProgress: composeProgress([
      filesProgress,
      getFilesHISProgress,
      queryProgress,
    ]),
    isLoadingFiles: isLoading(filesProgress),
  };
};

// Subscribes to live form values so the "Fields without changes" option is
// disabled the moment every populated field has been manually edited. Scoped
// to the dropdown so keystrokes don't force the whole modal to re-render.
function useHasUneditedFieldsToRefresh<T extends DocsFormName>({
  docsFillFormResult,
  previousFormData,
  savedMetadata,
}: {
  docsFillFormResult: DocsFormStatusResponse | null;
  previousFormData: DocsFormDataMapped<T> | null | undefined;
  savedMetadata: FormMetadata<T> | undefined;
}): boolean {
  const watchedValues = useWatch() as Partial<DocsFormDataMapped<T>>;

  return useMemo(() => {
    const currentValues = watchedValues ?? {};
    const keys = Object.keys(currentValues);
    if (keys.length === 0) return true;

    const editedFieldKeys = getManuallyEditedFieldKeys<T>({
      currentValues,
      previousFillResult: docsFillFormResult,
      previousFormData,
      savedMetadata,
    });

    return keys.some((key) => !editedFieldKeys.has(key));
  }, [watchedValues, docsFillFormResult, previousFormData, savedMetadata]);
}

const RefreshFieldsDropdown = <T extends DocsFormName>({
  docsFillFormResult,
  importFormData,
  previousFormData,
  refreshTriggerButton,
  savedMetadata,
}: {
  docsFillFormResult: DocsFormStatusResponse | null;
  importFormData: (mode?: RefreshMode) => Promise<void>;
  previousFormData: DocsFormDataMapped<T> | null | undefined;
  refreshTriggerButton: ReturnType<typeof Button>;
  savedMetadata: FormMetadata<T> | undefined;
}) => {
  const translations = useTranslations();
  const hasUneditedFieldsToRefresh = useHasUneditedFieldsToRefresh<T>({
    docsFillFormResult,
    previousFormData,
    savedMetadata,
  });

  return (
    <Dropdown
      ariaLabel={
        translations.extractDocs.splitView.header.buttonRefreshOptionsAriaLabel
      }
      DropdownTrigger={refreshTriggerButton}
      placement="bottom-end"
      sections={[
        {
          items: [
            {
              color: "primary",
              description:
                translations.extractDocs.dischargeLetter.refreshFields
                  .descriptionSelected,
              isDisabled: !hasUneditedFieldsToRefresh,
              key: REFRESH_MODE.UNEDITED_FIELDS,
              onPress: () => {
                importFormData(REFRESH_MODE.UNEDITED_FIELDS);
              },
              title:
                translations.extractDocs.dischargeLetter.refreshFields
                  .titleSelected,
            },
            {
              classNames: { description: "text-danger" },
              color: "danger",
              description:
                translations.extractDocs.dischargeLetter.refreshFields
                  .descriptionAll,
              key: REFRESH_MODE.ALL_FIELDS,
              onPress: () => {
                importFormData(REFRESH_MODE.ALL_FIELDS);
              },
              title:
                translations.extractDocs.dischargeLetter.refreshFields.titleAll,
            },
          ],
        },
      ]}
    />
  );
};

const DocsModalHeader = <T extends DocsFormName>({
  canImportData,
  docsFillFormResult,
  formName,
  hasNewExtractedDocuments,
  hasPreviousFormValue,
  header,
  importFormData,
  importFormProgress,
  isDischargeLetterForm,
  onClose,
  patient,
  previousFormData,
  savedMetadata,
}: {
  hasPreviousFormValue: boolean;
  hasNewExtractedDocuments: boolean;
  canImportData: boolean;
  docsFillFormResult: DocsFormStatusResponse | null;
  formName: DocsFormName;
  header: string;
  importFormData: (mode?: RefreshMode) => Promise<void>;
  importFormProgress: QueryProgress;
  isDischargeLetterForm: boolean;
  onClose: () => void;
  patient: Patient;
  previousFormData: DocsFormDataMapped<T> | null | undefined;
  savedMetadata: FormMetadata<T> | undefined;
}) => {
  const translations = useTranslations();
  const isFormFilled = getDocsFormIsFilled(
    hasPreviousFormValue,
    importFormProgress,
  );
  const showRefreshOptions = isFormFilled && isDischargeLetterForm;

  const refreshTriggerButton = (
    <Button
      color="primary"
      id={isFormFilled ? REIMPORT_AUCTION_ID : IMPORT_AUCTION_ID}
      isDisabled={!canImportData}
      isLoading={isLoading(importFormProgress)}
      size="sm"
      startContent={isFormFilled ? <RefreshCwIcon /> : <SparklesIcon />}
      trackEventProps={{ form_name: formName }}
      variant={isFormFilled && !hasNewExtractedDocuments ? "ghost" : "solid"}
      onPress={
        showRefreshOptions
          ? undefined
          : () => {
              importFormData();
            }
      }
    >
      {isFormFilled
        ? translations.extractDocs.splitView.header.buttonRefreshData
        : translations.extractDocs.splitView.header.buttonImportData}
    </Button>
  );

  return (
    <ModalHeader className="flex w-full items-center justify-between">
      <div className="flex flex-col gap-1">
        <p className="text-body-md font-semibold text-gray-600">{`${getPatientName(
          patient,
        )} (${patient.user_id})`}</p>
        <h1 className="text-heading-lg font-bold">{header}</h1>
      </div>
      <div className="flex gap-4">
        {showRefreshOptions ? (
          <RefreshFieldsDropdown<T>
            docsFillFormResult={docsFillFormResult}
            importFormData={importFormData}
            previousFormData={previousFormData}
            refreshTriggerButton={refreshTriggerButton}
            savedMetadata={savedMetadata}
          />
        ) : (
          refreshTriggerButton
        )}
        <Button
          color="primary"
          endContent={<XIcon />}
          id="close-modal"
          size="sm"
          variant="light"
          onPress={onClose}
        >
          {translations.extractDocs.splitView.header.buttonClose}
        </Button>
      </div>
    </ModalHeader>
  );
};

const DocsModalContent = ({
  auction,
  auctionForms,
  consultation,
  files,
  formName,
  patient,
}: {
  auction: Auction | undefined;
  consultation: ClinicalConsultation | undefined;
  auctionForms: AuctionForm[];
  formName: DocsFormName;
  files: { name: string; file: FileState }[];
  patient: Patient;
}) => {
  const translations = useTranslations();
  const [activeFeedback, setActiveFeedback] =
    useState<DocsFieldFeedback | null>(null);

  const {
    canImportData,
    config,
    confirmationChecked,
    docsFillFormResult,
    footerBanner,
    footerScreen,
    form,
    formData,
    handleBack,
    handleDeleteForm,
    handleOpenPdf,
    handleSendToHis,
    handleSubmit,
    hasExistingFormPdf,
    hasNewExtractedDocuments,
    hasPreviousFormValue,
    headerBannerAction,
    importFormData,
    importFormProgress,
    methods,
    onCloseHeaderBanner,
    onCloseNewExtractedDocumentsBanner,
    onSubmitProgress,
    progressDeleteForm,
    progressOpenPdf,
    progressSendToHis,
    progressUploadFormPdf,
    savedMetadata,
    setConfirmationChecked,
    setFooterBanner,
    setFooterScreen,
    setHeaderBannerAction,
    skipRequiredValidation,
    sourceInformation,
  } = useDocsForm({
    auction,
    auctionForms,
    consultation,
    files,
    formName,
    onFormSaved: () => setActiveFeedback(null),
    onSourceSelected: () => setActiveFeedback(null),
    patient,
  });
  const { setFormModal } = useRecareDocsContext();

  const handleFieldFeedback = (feedback: DocsFieldFeedback | null) => {
    if (feedback === null) {
      setActiveFeedback(null);
      return;
    }
    const fieldKey = feedback.fieldName as keyof DocsFormDataMapped<
      typeof formName
    >;
    const currentSourceField = sourceInformation.getSourceFields(fieldKey);
    const currentRating = currentSourceField?.rating;
    const isToggleOff =
      activeFeedback?.fieldName === feedback.fieldName &&
      currentRating === feedback.rating;
    if (isToggleOff) {
      sourceInformation.setFieldRating(feedback.fieldName, null);
      setActiveFeedback(null);
      return;
    }
    const isRatingChange =
      currentRating != null && currentRating !== feedback.rating;
    sourceInformation.setFieldRating(feedback.fieldName, feedback.rating);
    if (isRatingChange) {
      sourceInformation.setFieldRatingDescription(feedback.fieldName, "");
    }
    setActiveFeedback({
      ...feedback,
      ratingDescription: isRatingChange
        ? ""
        : currentSourceField?.rating_description ?? "",
    });
  };

  const handleSaveFeedback = (description: string) => {
    if (!activeFeedback) return;
    sourceInformation.setFieldRatingDescription(
      activeFeedback.fieldName,
      description,
    );
    setActiveFeedback(null);
  };

  return (
    <FormProvider {...methods}>
      <div className="flex h-full flex-col gap-0">
        <DocsModalHeader<typeof formName>
          canImportData={canImportData}
          docsFillFormResult={docsFillFormResult}
          formName={formName}
          hasNewExtractedDocuments={hasNewExtractedDocuments}
          hasPreviousFormValue={hasPreviousFormValue}
          header={config.header({ translations })}
          importFormData={importFormData}
          importFormProgress={importFormProgress}
          isDischargeLetterForm={isDischargeLetterFormName(formName)}
          patient={patient}
          previousFormData={formData}
          savedMetadata={savedMetadata}
          onClose={() => {
            if (
              methods.formState.isDirty &&
              headerBannerAction.banner !== HEADER_BANNERS.FORM_DIRTY_WARNING
            ) {
              setHeaderBannerAction({
                banner: HEADER_BANNERS.FORM_DIRTY_WARNING,
              });
            } else {
              setFormModal(null);
            }
          }}
        />
        <hr className="w-full border-gray-400" />
        <ModalBody className="flex h-full w-full flex-row gap-0 overflow-hidden p-0">
          <Form
            className="flex min-h-0 flex-1 flex-col gap-0"
            onSubmit={handleSubmit}
          >
            <div className="min-h-0 w-full overflow-y-auto">
              <DocsFormFields<typeof formName>
                auction={auction}
                config={config}
                formName={formName}
                headerBannerAction={headerBannerAction}
                importData={importFormData}
                importFormProgress={importFormProgress}
                shouldSkipRequiredValidation={skipRequiredValidation}
                sourceInformation={sourceInformation}
                onCloseHeaderBanner={onCloseHeaderBanner}
                onFieldFeedback={handleFieldFeedback}
              />
            </div>
            <FormFooter
              canPrint={config.config.canPrint}
              canSendToHis={config.config.canSendToHis}
              fileName={config.pdfConfig.downloadPdfFileName(translations)}
              footerBanner={footerBanner}
              footerScreen={footerScreen}
              form={form as AuctionForm | undefined}
              handleDeleteForm={async () => {
                await handleDeleteForm();
                sourceInformation.resetFeedback();
                setActiveFeedback(null);
              }}
              handleOpenPdf={handleOpenPdf}
              handleSendToHis={handleSendToHis}
              hasExistingFormPdf={hasExistingFormPdf}
              hasFeedbackChanges={sourceInformation.hasFeedbackChanges}
              hasPreviousFormValue={hasPreviousFormValue}
              isConfirmationChecked={confirmationChecked}
              progressDeleteForm={progressDeleteForm}
              progressOpenPdf={progressOpenPdf}
              progressSendToHis={progressSendToHis}
              progressUploadFormPdf={progressUploadFormPdf}
              setConfirmationChecked={setConfirmationChecked}
              setFooterBanner={setFooterBanner}
              setFooterScreen={setFooterScreen}
              onBack={handleBack}
              onSubmitProgress={onSubmitProgress}
            />
          </Form>
          {activeFeedback ? (
            <DocsFeedbackPanel
              key={activeFeedback.fieldName}
              activeFeedback={activeFeedback}
              onClose={() => handleFieldFeedback(null)}
              onSave={handleSaveFeedback}
            />
          ) : (
            <DocsModalSources
              files={files}
              formName={formName}
              hasNewExtractedDocuments={hasNewExtractedDocuments}
              patient={patient}
              resetSelectedSource={sourceInformation.resetSelectedSource}
              selectedSource={sourceInformation.selectedSource}
              onCloseNewExtractedDocumentsBanner={
                onCloseNewExtractedDocumentsBanner
              }
            />
          )}
        </ModalBody>
      </div>
    </FormProvider>
  );
};

export const RecareDocsModal = ({
  auction,
  consultation,
  patient,
}: {
  auction: Auction | undefined;
  consultation: ClinicalConsultation | undefined;
  patient: Patient;
}) => {
  const { formModal } = useRecareDocsContext();

  const [auctionFormsQueryProgress, auctionForms] = useGetAuctionForms({
    auctionId: auction?.id ?? 0,
    skip: formModal === null || !auction?.id,
  });

  const { files } = useFetchFiles({
    auction,
    consultation,
    patient,
  });

  if (!formModal) return null;

  return (
    <Modal
      classNames={{
        backdrop: "z-modal!",
        base: "max-h-[95vh] max-w-[1600px]! overflow-y-auto",
        wrapper: "z-modal!",
      }}
      hideCloseButton
      isDismissable={false}
      isOpen
    >
      <ModalContent className="flex h-[95vh] w-[95vw] flex-col">
        {isLoading([auctionFormsQueryProgress]) ? (
          <ModalBody className="flex h-full w-full items-center justify-center">
            <Spinner color="primary" size="lg" testId="spinner-loading-docs" />
          </ModalBody>
        ) : (
          <DocsModalContent
            auction={auction}
            auctionForms={auctionForms ?? []}
            consultation={consultation}
            files={files}
            formName={formModal}
            patient={patient}
          />
        )}
      </ModalContent>
    </Modal>
  );
};
